import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { supabase } from '@/lib/supabase';

const ContactoPage = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submittedFolio, setSubmittedFolio] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.email || !formData.mensaje) {
      setSubmitStatus('error');
      return;
    }

    if (formData.mensaje.length > 3000) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nombre', formData.nombre);
      formBody.append('email', formData.email);
      formBody.append('telefono', formData.telefono);
      formBody.append('servicio', formData.servicio);
      formBody.append('mensaje', formData.mensaje);

      const [formResponse, supabaseResult] = await Promise.all([
        fetch('https://readdy.ai/api/form/d6pntsba90d3mf3eg4m0', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }),
        supabase
          .from('kronos_submissions')
          .insert({
            nombre: formData.nombre,
            email: formData.email,
            telefono: formData.telefono || 'No proporcionado',
            servicio: formData.servicio || 'No especificado',
            mensaje: formData.mensaje,
          })
          .select('folio')
          .maybeSingle(),
      ]);

      if (formResponse.ok && !supabaseResult.error) {
        setSubmittedFolio(supabaseResult.data?.folio ?? null);
        setSubmitStatus('success');
        setFormData({ nombre: '', email: '', telefono: '', servicio: '', mensaje: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: 'Dirección',
      content: 'Prol. 5 de Mayo, 727. Lomas de Tarango, Álvaro Obregón, CDMX',
      link: 'https://maps.app.goo.gl/V9Y9r1gSCh4ARwjr9',
    },
    {
      icon: 'ri-phone-line',
      title: 'Teléfono',
      content: '56 1402 5070',
      link: 'tel:+525614025070',
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      content: 'ventas@kronoscc.com',
      link: 'mailto:ventas@kronoscc.com',
    },
    {
      icon: 'ri-time-line',
      title: 'Horario',
      content: 'Lunes a Viernes: 9:00 AM - 6:00 PM',
      link: null,
    },
  ];

  const services = [
    'Conciliaciones',
    'Contabilidad General',
    'Servicios Fiscales',
    'Nóminas',
    'Consultoría Administrativa',
    'Tecnología',
    'Otro',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <WhatsAppButton />

      <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary via-primary-dark to-primary-light">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mb-6">Contacto</h1>
          <p className="font-body text-xl text-white/90 max-w-3xl mx-auto">
            Estamos aquí para ayudarte. Contáctanos y resolveremos todas tus dudas
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading font-bold text-3xl text-primary mb-6">Solicita más información</h2>
              <div className="w-24 h-1 bg-gold mb-8"></div>
              <p className="font-body text-base text-gray-700 mb-8 leading-relaxed">
                Completa el formulario y nos pondremos en contacto contigo lo antes posible. También puedes llamarnos directamente o visitarnos en nuestra oficina.
              </p>

              <form id="contact-form" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div>
                  <label htmlFor="nombre" className="block font-body text-sm font-semibold text-gray-700 mb-2">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-body text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block font-body text-sm font-semibold text-gray-700 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                    placeholder="55 1234 5678"
                  />
                </div>

                <div>
                  <label htmlFor="servicio" className="block font-body text-sm font-semibold text-gray-700 mb-2">
                    Servicio de interés
                  </label>
                  <select
                    id="servicio"
                    name="servicio"
                    value={formData.servicio}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                  >
                    <option value="">Selecciona un servicio</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="mensaje" className="block font-body text-sm font-semibold text-gray-700 mb-2">
                    Mensaje <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    required
                    maxLength={3000}
                    rows={7}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm resize-none"
                    placeholder="Cuéntanos cómo podemos ayudarte (máximo 3,000 caracteres)"
                  ></textarea>
                  <p className={`text-xs mt-2 ${formData.mensaje.length > 2800 ? 'text-amber-500' : 'text-gray-500'}`}>
                    {formData.mensaje.length.toLocaleString('es-MX')}/3,000 caracteres
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg px-5 py-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-green-600 text-base"></i>
                      </div>
                      <div>
                        <p className="font-body text-sm font-semibold text-green-800 mb-1">
                          ¡Mensaje enviado con éxito!
                        </p>
                        <p className="font-body text-sm text-green-700 mb-2">
                          Nos pondremos en contacto contigo a la brevedad.
                        </p>
                        {submittedFolio && (
                          <div className="mt-2 pt-2 border-t border-green-200">
                            <p className="font-body text-xs text-green-600 mb-1">Número de folio:</p>
                            <span className="font-mono font-bold text-sm text-green-900 tracking-widest bg-green-100 px-3 py-1 rounded-md select-all">
                              {submittedFolio}
                            </span>
                            <p className="font-body text-xs text-green-600 mt-1">
                              Guarda este número para dar seguimiento a tu solicitud.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Hubo un error al enviar el mensaje. Verifica los campos e intenta nuevamente.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-primary hover:bg-primary-dark text-white font-heading font-bold text-base rounded-lg transition-all duration-300 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                </button>
              </form>
            </div>

            <div>
              <h2 className="font-heading font-bold text-3xl text-primary mb-6">Información de Contacto</h2>
              <div className="w-24 h-1 bg-gold mb-8"></div>

              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${info.icon} text-xl text-primary`}></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-base text-gray-800 mb-2">{info.title}</h3>
                        {info.link ? (
                          <a
                            href={info.link}
                            target={info.link.startsWith('http') ? '_blank' : undefined}
                            rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="font-body text-sm text-gray-600 hover:text-primary transition-colors"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="font-body text-sm text-gray-600">{info.content}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-primary to-primary-dark rounded-xl p-8 text-white">
                <h3 className="font-heading font-bold text-2xl mb-4">¿Prefieres hablar directamente?</h3>
                <p className="font-body text-sm text-white/90 mb-6">
                  Llámanos o envíanos un WhatsApp y con gusto te atenderemos de inmediato.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="tel:+525614025070"
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-white text-primary font-heading font-bold text-sm rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    <span>Llamar Ahora</span>
                  </a>
                  <a
                    href="https://wa.me/525614025070"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-500 text-white font-heading font-bold text-sm rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line"></i>
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-4xl text-primary mb-4">Encuéntranos</h2>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
            <p className="font-body text-lg text-gray-700">Visítanos en nuestra oficina</p>
          </div>

          <div className="rounded-xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3763.8!2d-99.2489!3d19.3738!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDIyJzI1LjciTiA5OcKwMTQnNTYuMCJX!5e0!3m2!1ses!2smx!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación de Kronos Consultoría Contable"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactoPage;